import type { AppConfigMasteryLevelsType } from "@memorang/constants/config/schema";
import { formatSecondsToUTCtime } from "@memorang/helpers/common";
import type { ItemTypeDetailStats } from "@memorang/helpers/report";
import type { BaseItemSchema } from "@memorang/types";
import { useTranslation } from "react-i18next";
import { Dialog, Text } from "react-native-paper";
import { questionTypeIconMap } from "../../../constants";
import type { IconType, LevelIconName } from "../../../icon";
import { useAppTheme } from "../../../useAppTheme";
import { useDevice } from "../../../useDevice";
import { AppIcon } from "../../AppIcon";
import Box from "../../Box";
import Button from "../../Button";
import type { ConnectedLevel } from "../../ConnectedLevels";
import { ConnectedLevels } from "../../ConnectedLevels";
import DialogWrapper from "../../DialogWrapper";
import { MasteryChip, useMasteryLevelColor } from "../../MasteryChip";
import { StatTile } from "../../StatTile";

type MasteryLevel = AppConfigMasteryLevelsType[number];

const SECONDS_PER_MINUTE = 60;

type StatTileData = {
	key: string;
	icon: LevelIconName;
	label: string;
	value: string;
	suffix?: string;
};

const SessionReportItemTypeStats = ({
	stats,
}: {
	stats: ItemTypeDetailStats;
}) => {
	const { t } = useTranslation();
	const { isMobile } = useDevice();

	const tiles: StatTileData[] = [
		{
			key: "answered",
			icon: "card-multiple-outline",
			label: t("report.itemTypeDetail.stats.answered", {
				count: stats.numTotal,
			}),
			value: String(stats.numAnswered),
			suffix: t("session.results.summary.stats.ofTotal", {
				total: stats.numTotal,
			}),
		},
	];

	if (stats.pointsEarned != null) {
		tiles.push({
			key: "pointsEarned",
			icon: "star-circle-outline",
			label: t("session.results.summary.stats.pointsEarned", {
				count: stats.pointsTotal ?? stats.pointsEarned,
			}),
			value: String(stats.pointsEarned),
			suffix:
				stats.pointsTotal != null
					? t("session.results.summary.stats.ofPointsTotal", {
							total: stats.pointsTotal,
						})
					: undefined,
		});
	}

	if (stats.avgTimeSeconds != null) {
		const isUnderAMinute = stats.avgTimeSeconds < SECONDS_PER_MINUTE;
		tiles.push({
			key: "avgTime",
			icon: "clock-outline",
			label: t("report.itemTypeDetail.stats.avgTime"),
			value: isUnderAMinute
				? String(stats.avgTimeSeconds)
				: formatSecondsToUTCtime(stats.avgTimeSeconds),
			suffix: isUnderAMinute
				? t("common.sec", { count: stats.avgTimeSeconds })
				: undefined,
		});
	}

	return (
		<Box flexDirection="row" gap={isMobile ? 4 : 8} width="100%">
			{tiles.map(({ key, icon, label, value, suffix }) => (
				<Box key={key} flex={1} minWidth={0} style={{ flexBasis: 0 }}>
					<StatTile icon={icon} label={label} value={value} suffix={suffix} />
				</Box>
			))}
		</Box>
	);
};

type PerformanceIndicatorProps = {
	masteryLevel?: MasteryLevel;
	levels?: ConnectedLevel[];
};

const SessionReportItemTypePerformanceIndicator = ({
	masteryLevel,
	levels,
}: PerformanceIndicatorProps) => {
	const { t } = useTranslation();
	const masteryColor = useMasteryLevelColor(masteryLevel);

	if (levels?.length) {
		return <ConnectedLevels levels={levels} />;
	}

	if (!masteryLevel || !masteryColor) {
		return null;
	}

	return (
		<MasteryChip label={t(masteryLevel.alias.title)} color={masteryColor} />
	);
};

export type SessionReportItemTypeDetailSelection = {
	questionType: string;
	skill?: string;
	numItems: number;
	masteryLevel?: MasteryLevel;
	levels?: ConnectedLevel[];
	stats: ItemTypeDetailStats;
	reviewItems: BaseItemSchema[];
};

type Props = {
	visible: boolean;
	onClose: () => void;
	questionType: string;
	icon?: IconType;
	masteryLevel?: MasteryLevel;
	levels?: ConnectedLevel[];
	numItems: number;
	stats: ItemTypeDetailStats;
	onReview: () => void;
	loading?: boolean;
};

export const SessionReportItemTypeDetailDialog = ({
	visible,
	onClose,
	questionType,
	icon,
	masteryLevel,
	levels,
	numItems,
	stats,
	onReview,
	loading = false,
}: Props) => {
	const theme = useAppTheme();
	const { t, i18n } = useTranslation();
	const resolvedIcon = icon ?? questionTypeIconMap[questionType];
	const descriptionKey = `questionTypes.descriptions.${questionType}`;
	const description = i18n.exists(descriptionKey)
		? t(descriptionKey)
		: undefined;

	return (
		<DialogWrapper
			visible={visible}
			handleClose={onClose}
			showCloseButton
			maxWidth={560}
			width="90%"
		>
			<Box paddingTop={42} paddingHorizontal={16} paddingBottom={16} gap={16}>
				<Box alignItems="center" gap={8}>
					{resolvedIcon ? (
						<Box
							width={42}
							height={42}
							alignItems="center"
							justifyContent="center"
						>
							<AppIcon
								name={resolvedIcon}
								size={24}
								color={theme.colors.onSurface}
							/>
						</Box>
					) : null}
					<Text variant="headlineSmall" style={{ textAlign: "center" }}>
						{questionType}
					</Text>
					<SessionReportItemTypePerformanceIndicator
						masteryLevel={masteryLevel}
						levels={levels}
					/>
				</Box>
				{description ? (
					<Text
						variant="bodySmall"
						style={{ textAlign: "center", fontStyle: "italic" }}
					>
						{description}
					</Text>
				) : null}
				<SessionReportItemTypeStats stats={stats} />
			</Box>
			<Dialog.Actions>
				<Button
					mode="outlined"
					onPress={onReview}
					loading={loading}
					disabled={loading}
				>
					{t("report.itemTypeDetail.review", { count: numItems })}
				</Button>
			</Dialog.Actions>
		</DialogWrapper>
	);
};

export default SessionReportItemTypeDetailDialog;
